import React, { useEffect, useState } from 'react';
import { Clock, Plus, Trash2, RotateCw, Users } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import ShiftCalendar from './ShiftCalendar';

const CYCLE_LABELS = {
  weekly: 'Every week',
  weekly_2: 'Every 2 weeks',
  monthly: 'Every month',
};

const prettyRole = (r) =>
  (r || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const Shifts = () => {
  const { token } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // New-assignment form
  const [form, setForm] = useState({
    user_id: '',
    template_id: '',
    effective_from: new Date().toISOString().slice(0, 10),
    effective_to: '',
    rotation_enabled: false,
    rotation_cycle: 'weekly_2',
    alternate_template_id: '',
  });

  const [view, setView] = useState('assignments'); // 'assignments' | 'calendar'
  const [filterUser, setFilterUser] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterStatus, setFilterStatus] = useState('active'); // active | expired | all
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      const [tplRes, usrRes, asgRes] = await Promise.all([
        api.get('/admin/shift-templates', headers),
        api.get('/shifts/manageable-users', headers),
        api.get('/shifts/assignments', headers),
      ]);
      setTemplates(tplRes.data?.templates || []);
      setUsers(usrRes.data || []);
      setAssignments(asgRes.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load shifts data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadAll();
  }, [token]);

  const reloadAssignments = async () => {
    try {
      const res = await api.get('/shifts/assignments', authHeader);
      setAssignments(res.data || []);
    } catch (e) {
      /* ignore */
    }
  };

  const submit = async () => {
    if (!form.user_id || !form.template_id) {
      alert('Choose a user and a shift.');
      return;
    }
    if (form.rotation_enabled && !form.alternate_template_id) {
      alert('Pick the alternate shift for rotation.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/shifts/assignments', {
        user_id: form.user_id,
        template_id: form.template_id,
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        rotation: {
          enabled: form.rotation_enabled,
          cycle: form.rotation_cycle,
          alternate_template_id: form.rotation_enabled ? form.alternate_template_id : null,
        },
      }, authHeader);
      setForm((f) => ({
        ...f,
        user_id: '',
        rotation_enabled: false,
        alternate_template_id: '',
      }));
      await reloadAssignments();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this shift assignment?')) return;
    try {
      await api.delete(`/shifts/assignments/${id}`, authHeader);
      setAssignments((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to remove assignment');
    }
  };

  // An assignment's period is "completed" when effective_to is set and in the past.
  const todayIso = new Date().toISOString().slice(0, 10);
  const isExpired = (a) => !!a.effective_to && a.effective_to < todayIso;

  const filteredAssignments = assignments.filter((a) =>
    (!filterUser || a.user_id === filterUser) &&
    (!filterTemplate || String(a.template_id) === String(filterTemplate)) &&
    (filterStatus === 'all' ||
      (filterStatus === 'active' && !isExpired(a)) ||
      (filterStatus === 'expired' && isExpired(a)))
  );

  const totalPages = Math.max(1, Math.ceil(filteredAssignments.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visibleAssignments = filteredAssignments.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const fromRow = filteredAssignments.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const toRow = Math.min(safePage * PAGE_SIZE, filteredAssignments.length);

  const tplById = (id) => templates.find((t) => String(t.id) === String(id));

  // Reset to the first page whenever a filter changes.
  useEffect(() => { setPage(1); }, [filterUser, filterTemplate, filterStatus]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-gray-500">Loading shifts…</div>
      </div>
    );
  }

  return (
    <div className="p-6 overflow-x-hidden">
      <div className="flex items-center gap-2 mb-1">
        <Clock className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-800">Shifts</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Assign shift timings to users. Some users keep a fixed shift; others rotate every 2 weeks
        or monthly.
      </p>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {templates.length === 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-3 text-sm">
          No shift timings defined yet. An admin can add them in <span className="font-medium">Settings → Shift Timings</span>.
        </div>
      )}

      {/* Calendar vs the assignment list. The calendar answers "what is this
          person doing this month"; the list answers "what rules are set". */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[['assignments', 'Assignments'], ['calendar', 'Calendar View']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              view === id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >{label}</button>
        ))}
      </div>

      {view === 'calendar' && <ShiftCalendar users={users} />}

      {view === 'assignments' && (
      <>
      {/* ── New assignment ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8 max-w-full">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Assign a Shift
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">User</label>
            <select
              value={form.user_id}
              onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
            >
              <option value="">Select user…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({prettyRole(u.role)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Shift</label>
            <select
              value={form.template_id}
              onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
            >
              <option value="">Select shift…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.start}–{t.end})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">To (optional)</label>
              <input
                type="date"
                value={form.effective_to}
                onChange={(e) => setForm((f) => ({ ...f, effective_to: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Rotation */}
        <div className="mt-4 border-t border-gray-100 pt-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.rotation_enabled}
              onChange={(e) => setForm((f) => ({ ...f, rotation_enabled: e.target.checked }))}
            />
            <RotateCw className="w-4 h-4 text-gray-500" />
            Rotate this shift with another
          </label>

          {form.rotation_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Alternate shift</label>
                <select
                  value={form.alternate_template_id}
                  onChange={(e) => setForm((f) => ({ ...f, alternate_template_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                >
                  <option value="">Select alternate…</option>
                  {templates
                    .filter((t) => String(t.id) !== String(form.template_id))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.start}–{t.end})
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Swap every</label>
                <select
                  value={form.rotation_cycle}
                  onChange={(e) => setForm((f) => ({ ...f, rotation_cycle: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                >
                  <option value="weekly">Every week</option>
                  <option value="weekly_2">Every 2 weeks</option>
                  <option value="monthly">Every month</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4">
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving…' : 'Assign Shift'}
          </button>
        </div>
      </div>

      {/* ── Current assignments ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <Users className="w-4 h-4" /> Current Assignments
          </h2>
          <div className="flex gap-2">
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">All users</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <select
              value={filterTemplate}
              onChange={(e) => setFilterTemplate(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">All shifts</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              title="Show active (not yet completed) assignments, completed ones, or all"
            >
              <option value="active">Active only</option>
              <option value="expired">Completed period</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-left">
                <th className="px-4 py-2 font-semibold text-gray-700">User</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Shift</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Timing</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Period</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Rotation</th>
                <th className="px-4 py-2 font-semibold text-gray-700 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleAssignments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                    {assignments.length === 0
                      ? 'No shift assignments yet.'
                      : filterStatus === 'active'
                        ? 'No active shift assignments for this filter.'
                        : 'No shift assignments match this filter.'}
                  </td>
                </tr>
              ) : (
                visibleAssignments.map((a) => {
                  const tpl = tplById(a.template_id);
                  const color = tpl?.color || '#93c5fd';
                  return (
                    <tr key={a.id}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-800">{a.user_name}</div>
                        <div className="text-xs text-gray-500">{prettyRole(a.user_role)}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs font-medium text-gray-800"
                          style={{ backgroundColor: color }}
                        >
                          {a.template_name || a.template_id}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {a.start && a.end ? `${a.start} – ${a.end}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {a.effective_from || '—'}
                        {a.effective_to ? ` → ${a.effective_to}` : ' → ongoing'}
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {a.rotation?.enabled ? (
                          <span className="text-xs">
                            ↔ {a.rotation.alternate_template_name || a.rotation.alternate_template_id}
                            <span className="text-gray-400"> · {CYCLE_LABELS[a.rotation.cycle] || a.rotation.cycle}</span>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Fixed</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => remove(a.id)}
                          className="text-red-600 hover:text-red-800 inline-flex items-center"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredAssignments.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-500">
              Showing {fromRow}–{toRow} of {filteredAssignments.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >← Prev</button>
              <span className="text-sm text-gray-600 px-2">Page {safePage} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >Next →</button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default Shifts;
